import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Crown } from "lucide-react";

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center bg-cream" data-testid="auth-loading">
      <div className="flex flex-col items-center gap-3 text-purpleK">
        <Crown size={44} className="animate-bob" />
        <span className="font-display font-semibold">Entering the Kingdom…</span>
      </div>
    </div>
  );
}

// role: "parent" | "admin" | "child"
// allowChild: also permit an authenticated child session (used for /kingdom/play/:childId)
export default function ProtectedRoute({ children, role, allowChild }) {
  const { user, child } = useAuth();

  // Child-only routes
  if (role === "child") {
    if (child === undefined) return <Loading />;
    if (!child) return <Navigate to="/kingdom/login" replace />;
    return children;
  }

  // Educator area: any signed-in account may enter; the page itself shows a friendly
  // access gate for non-verified-educators (never a hard error).
  if (role === "educator") {
    if (user === undefined || child === undefined) return <Loading />;
    if (child) return <Navigate to={`/kingdom/play/${child.id}`} replace />;
    if (!user) return <Navigate to="/kingdom/login?next=/teacher" replace />;
    return children;
  }

  // Routes that allow EITHER parent/admin OR the child themselves
  if (allowChild) {
    if (user === undefined || child === undefined) return <Loading />;
    if (child) return children;
  } else if (user === undefined) {
    return <Loading />;
  }

  // Parent/admin routes: a signed-in CHILD must be sent to their own Kingdom, never the parent area.
  if (user === null) {
    if (child === undefined) return <Loading />;
    if (child) return <Navigate to={`/kingdom/play/${child.id}`} replace />;
    return <Navigate to="/kingdom/login" replace />;
  }
  if (role === "admin" && user.role !== "admin") return <Navigate to="/kingdom" replace />;
  if (role === "parent" && !["parent", "admin"].includes(user.role)) return <Navigate to="/kingdom/login" replace />;
  return children;
}
